import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
} from "@chakra-ui/react";
import { HiCheck, HiChevronDown } from "react-icons/hi";
import { translate } from "ui/i18n";
import { INCheckbox } from "ui/pages/app/components/inputs/INCheckbox";
import {
  categoryFilters,
  contextOfUseFilters,
  durationFilters,
  levelFilters,
  modalitiesFilters,
  objectiveFilters,
  supportsObjectiveFilters,
  typesFilters,
} from "ui/pages/app/components/activities/activitiesFiltersValues";
import { INRangeSlider } from "ui/pages/app/components/inputs/INRangeSlider";
import {
  DurationFilter,
  ActivitiesPageFilters,
} from "domain/entities/activities.entities";
import { INRadioButton } from "ui/pages/app/components/inputs/INRadioButton";
import { PropsWithChildren, useEffect, useState } from "react";
import { INInput } from "ui/pages/app/components/inputs/INInput";
import { CustomTooltip } from "ui/pages/app/components/CustomTooltip";
import { selectUserId } from "domain/auth/auth.selectors";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { selectOnBoardingValues } from "domain/on-boarding/on-boarding.selectors";
import { updateOnBoardingValue } from "ui/utils/on-boarding-storage";

interface IProps {
  filters: ActivitiesPageFilters & { favorite?: boolean };
  setFilters: (
    filters: ActivitiesPageFilters & {
      [key: string]: string | string[] | boolean | DurationFilter | number;
      favorite?: boolean;
    }
  ) => void;
  fetchActivities?: () => void;
  activitiesType: "techniques" | "seances" | "tutorials" | "visual-supports";
  maxDuration?: number;
  minDuration?: number;
  className?: string;
  resetFilters: () => void;
  favoriteFilter?: boolean;
  setFavoriteFilter?: (favorite: boolean) => void;
}

export function DesktopActivitiesFilters({
  filters,
  setFilters,
  fetchActivities,
  activitiesType,
  maxDuration,
  minDuration,
  className,
  resetFilters,
  favoriteFilter,
  setFavoriteFilter,
}: IProps) {
  const [isContextOfUseOpen, setIsContextOfUseOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isObjectiveOpen, setIsObjectiveOpen] = useState(false);
  const [isDurationOpen, setIsDurationOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [isModalityOpen, setIsModalityOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isLevelOpen, setIsLevelOpen] = useState(false);

  const focusParticipants = () => {
    setTimeout(
      () => document.getElementById("participants-input")?.focus(),
      100
    );
  };

  return (
    <div>
      <div className={`mt-8 flex space-x-4 ${className || ""}`}>
        {/* Favorite filter */}
        {favoriteFilter !== undefined && setFavoriteFilter && (
          <Popover
            isOpen={isFavoritesOpen}
            onOpen={() => setIsFavoritesOpen(true)}
            onClose={() => setIsFavoritesOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {favoriteFilter && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    <HiCheck color="white" size="12" />
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Favorites")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isFavoritesOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                <INCheckbox
                  inputName={"favorite"}
                  text={translate("ns1:Activities.Buttons.MyFavorites")}
                  value={favoriteFilter}
                  onChange={() => setFavoriteFilter(!favoriteFilter)}
                  bold
                />
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() => setFavoriteFilter(false)}
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* ContextOfUse filter */}
        {activitiesType === "techniques" && (
          <Popover
            isOpen={isContextOfUseOpen}
            onOpen={() => setIsContextOfUseOpen(true)}
            onClose={() => setIsContextOfUseOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.contexts?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.contexts?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.ContextOfUse")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isContextOfUseOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {contextOfUseFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.contexts?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        contexts: filters.contexts?.includes(filter.value)
                          ? filters.contexts.filter((c) => c !== filter.value)
                          : [...filters.contexts, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      contexts: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Type filter */}
        {activitiesType === "techniques" && (
          <Popover
            isOpen={isTypeOpen}
            onOpen={() => setIsTypeOpen(true)}
            onClose={() => setIsTypeOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.types?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.types?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Type")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isTypeOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {typesFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.types?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        types: filters.types?.includes(filter.value)
                          ? filters.types.filter((c) => c !== filter.value)
                          : [...filters.types, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      types: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Technique Objective filter */}
        {activitiesType === "techniques" && (
          <Popover
            isOpen={isObjectiveOpen}
            onOpen={() => setIsObjectiveOpen(true)}
            onClose={() => setIsObjectiveOpen(false)}
            placement="bottom-start"
          >
            <ObjectivesTooltip>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.techObjectives?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.techObjectives?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Objective")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isObjectiveOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </ObjectivesTooltip>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {objectiveFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.techObjectives?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        techObjectives: filters.techObjectives?.includes(
                          filter.value
                        )
                          ? filters.techObjectives.filter(
                              (c) => c !== filter.value
                            )
                          : [...filters.techObjectives, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      techObjectives: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Seance Objective filter */}
        {activitiesType === "seances" && (
          <Popover
            isOpen={isObjectiveOpen}
            onOpen={() => setIsObjectiveOpen(true)}
            onClose={() => setIsObjectiveOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.seanceObjectives?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.seanceObjectives?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Objective")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isObjectiveOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {objectiveFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.seanceObjectives?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        seanceObjectives: filters.seanceObjectives?.includes(
                          filter.value
                        )
                          ? filters.seanceObjectives.filter(
                              (c) => c !== filter.value
                            )
                          : [...filters.seanceObjectives, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      seanceObjectives: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Support Objective filter */}
        {activitiesType === "visual-supports" && (
          <Popover
            isOpen={isObjectiveOpen}
            onOpen={() => setIsObjectiveOpen(true)}
            onClose={() => setIsObjectiveOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.supportObjectives?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.supportObjectives?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Objective")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isObjectiveOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {supportsObjectiveFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.supportObjectives?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        supportObjectives: filters.supportObjectives?.includes(
                          filter.value
                        )
                          ? filters.supportObjectives.filter(
                              (c) => c !== filter.value
                            )
                          : [...filters.supportObjectives, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      supportObjectives: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Duration filter */}
        {activitiesType === "techniques" && (
          <Popover
            isOpen={isDurationOpen}
            onOpen={() => setIsDurationOpen(true)}
            onClose={() => setIsDurationOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {(filters.duration?.$gte !== minDuration ||
                  filters.duration?.$lte !== maxDuration) && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    <HiCheck color="white" size="12" />
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Duration")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isDurationOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className=" rounded-[20px] bg-white p-5 shadow-lg">
                <INRangeSlider
                  min={minDuration}
                  max={maxDuration}
                  duration={filters.duration}
                  setDuration={(duration) =>
                    setFilters({
                      ...filters,
                      duration,
                    })
                  }
                  width={200}
                  step={5}
                  fetchActivities={fetchActivities}
                  resetButton
                />
              </div>
            </PopoverContent>
          </Popover>
        )}

        {activitiesType === "seances" && (
          <Popover
            isOpen={isDurationOpen}
            onOpen={() => setIsDurationOpen(true)}
            onClose={() => setIsDurationOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.selectedDuration !== "" && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    <HiCheck color="white" size="12" />
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Duration")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isDurationOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="rounded-[20px] bg-white p-5 shadow-lg">
                <RadioGroup
                  onChange={(value) =>
                    setFilters({ ...filters, selectedDuration: value })
                  }
                  value={filters.selectedDuration}
                  className="flex flex-col space-y-4"
                >
                  {durationFilters.map((filter) => (
                    <INRadioButton
                      key={filter.value}
                      text={filter.text}
                      value={filter.value}
                      selectedValue={filters.selectedDuration}
                    />
                  ))}
                </RadioGroup>
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({ ...filters, selectedDuration: "" })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Participants filter */}
        {(activitiesType === "seances" || activitiesType === "techniques") && (
          <Popover
            isOpen={isParticipantsOpen}
            onOpen={() => setIsParticipantsOpen(true)}
            onClose={() => setIsParticipantsOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button
                className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold"
                onClick={focusParticipants}
              >
                {filters.participants && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    <HiCheck color="white" size="12" />
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Participants")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isParticipantsOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="rounded-[20px] bg-white p-5 shadow-lg">
                <INInput
                  value={filters.participants}
                  onChange={(value) =>
                    setFilters({ ...filters, participants: value })
                  }
                  placeholder="Ex: 10"
                  type="number"
                  min={4}
                  max={50}
                  id="participants-input"
                  inputClassName="border-0 !p-0"
                  height="30px"
                />
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() => setFilters({ ...filters, participants: "" })}
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Modality filter */}
        {(activitiesType === "seances" || activitiesType === "techniques") && (
          <Popover
            isOpen={isModalityOpen}
            onOpen={() => setIsModalityOpen(true)}
            onClose={() => setIsModalityOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.modalities?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.modalities?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Modality")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isModalityOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {modalitiesFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.modalities?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        modalities: filters.modalities?.includes(filter.value)
                          ? filters.modalities.filter((c) => c !== filter.value)
                          : [...filters.modalities, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      modalities: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Category filter */}
        {activitiesType === "tutorials" && (
          <Popover
            isOpen={isCategoryOpen}
            onOpen={() => setIsCategoryOpen(true)}
            onClose={() => setIsCategoryOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.categories?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.categories?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Category")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isCategoryOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {categoryFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.categories?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        categories: filters.categories?.includes(filter.value)
                          ? filters.categories.filter((c) => c !== filter.value)
                          : [...filters.categories, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      categories: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Level filter */}
        {activitiesType === "techniques" && (
          <Popover
            isOpen={isLevelOpen}
            onOpen={() => setIsLevelOpen(true)}
            onClose={() => setIsLevelOpen(false)}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <button className="flex items-center rounded-xl border border-darkGrey-200 bg-white px-[18px] py-3 text-sm font-bold">
                {filters.level?.length > 0 && (
                  <span className="me-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs font-semibold text-white">
                    {filters.level?.length}
                  </span>
                )}
                {translate("ns1:Activities.Buttons.Level")}
                <HiChevronDown
                  size={22}
                  className={`ms-1 ${
                    isLevelOpen ? "popover-open" : "popover-close"
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              style={{
                isolation: "isolate",
                zIndex: 100,
                width: "fit-content",
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <div className="flex flex-col space-y-4 rounded-[20px] bg-white p-5 shadow-lg">
                {levelFilters.map((filter) => (
                  <INCheckbox
                    key={filter.value}
                    inputName={filter.value}
                    text={filter.text}
                    value={filters.level?.includes(filter.value)}
                    onChange={() =>
                      setFilters({
                        ...filters,
                        level: filters.level?.includes(filter.value)
                          ? filters.level.filter((c) => c !== filter.value)
                          : [...filters.level, filter.value],
                      })
                    }
                    bold
                  />
                ))}
                <button
                  className="mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      level: [],
                    })
                  }
                >
                  {translate("ns1:Button.Reset")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <button
        className="ms-2 mt-4 w-fit text-sm font-bold text-darkGrey-700 underline"
        onClick={resetFilters}
      >
        {translate("ns1:Button.ResetFilters")}
      </button>
    </div>
  );
}

const ObjectivesTooltip = ({ children }: PropsWithChildren) => {
  const userId = useAppSelector(selectUserId);
  const dispatch = useAppDispatch();
  const onBoardingValues = useAppSelector(selectOnBoardingValues);

  const closeObjectivesTooltip = () => {
    if (!onBoardingValues.closedObjectiveAdvice) {
      updateOnBoardingValue(userId, "closedObjectiveAdvice", dispatch);
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", closeObjectivesTooltip);
    return () => {
      closeObjectivesTooltip();
    };
  }, []); //eslint-disable-line

  return onBoardingValues.closedObjectiveAdvice ? (
    <PopoverTrigger>{children}</PopoverTrigger>
  ) : (
    <CustomTooltip
      open
      onClose={closeObjectivesTooltip}
      title={translate("ns1:OnBoarding.Tooltips.ObjectiveTitle")}
      text={translate("ns1:OnBoarding.Tooltips.ObjectiveText")}
    >
      {children}
    </CustomTooltip>
  );
};
